import { useCallback, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import debounce from "lodash.debounce";
import { FlotiqApi } from "@jti/rest-api";
import { getFiltersBaseOnFlotiqResponse, getLatLngAndRadius, getStoresFilteredByProductAttributes, getStoresFilteredBySelectedFilters, getTransformedMarkers, } from "../../../../components/StoreLocator/utils";
import { useConfig } from "../../../../providers/useConfig";
const DEFAULT_NUMBER_OF_STORES = 1000;
export const useFlotiqStores = (params) => {
    const { cartItems, filterByCartAvailabilityEnabled, filtersTranslations, storesLimit = DEFAULT_NUMBER_OF_STORES, activeStore, activeStoreId: activeStoreIdFromParams, flotiqApiKey, flotiqContentType, } = params || {};
    const { FLOTIQ_API_URL, FLOTIQ_API_KEY, STORE_LOCATOR_CONTENT_TYPE } = useConfig();
    const [stores, setStores] = useState([]);
    const [activeStoreId, setActiveStoreId] = useState(activeStoreIdFromParams);
    const [filteredStores, setFilteredStores] = useState([]);
    const [userMarkerPosition, setUserMarkerPosition] = useState();
    const [isNoMatchingFlotiqStores, setIsNoMatchingFlotiqStores] = useState(false);
    const [filters, setFilters] = useState([]);
    const [isFiltersOpened, setIsFiltersOpened] = useState(false);
    const [selectedFilters, setSelectedFilters] = useState({});
    const flotiqApi = useMemo(() => new FlotiqApi(FLOTIQ_API_URL, flotiqApiKey || FLOTIQ_API_KEY, flotiqContentType || STORE_LOCATOR_CONTENT_TYPE), [FLOTIQ_API_KEY, FLOTIQ_API_URL, STORE_LOCATOR_CONTENT_TYPE, flotiqApiKey, flotiqContentType]);
    const isFiltersAvailable = useMemo(() => {
        return filters.some((filter) => filter.filters.length > 0);
    }, [filters]);
    const selectedFiltersNumber = useMemo(() => Object.values(selectedFilters).filter(({ selected }) => selected).length, [selectedFilters]);
    const handlefilterStores = useCallback(() => {
        if (filterByCartAvailabilityEnabled && !cartItems.length) {
            setFilteredStores([]);
            return;
        }
        const storesFilteredBySelectedFilters = isFiltersAvailable
            ? getStoresFilteredBySelectedFilters(stores, selectedFilters)
            : stores;
        const storesFilteredByProductAttributes = filterByCartAvailabilityEnabled
            ? getStoresFilteredByProductAttributes(storesFilteredBySelectedFilters, cartItems, filterByCartAvailabilityEnabled)
            : storesFilteredBySelectedFilters;
        if (storesFilteredBySelectedFilters.length && !storesFilteredByProductAttributes.length) {
            setIsNoMatchingFlotiqStores(true);
            setFilteredStores(storesFilteredBySelectedFilters);
        }
        else {
            setFilteredStores(storesFilteredByProductAttributes);
        }
    }, [stores, cartItems, filterByCartAvailabilityEnabled, isFiltersAvailable, selectedFilters]);
    const handleLoadMarkersFromApi = useCallback(async (map) => {
        if (activeStore) {
            setStores([activeStore]);
            return;
        }
        const { lat, lng, radius } = getLatLngAndRadius(map);
        try {
            const response = await flotiqApi.getStores(radius, lat, lng, storesLimit);
            if (!response.data) {
                return;
            }
            const transformedMarkers = getTransformedMarkers(response.data);
            const filtersBaseOnResult = getFiltersBaseOnFlotiqResponse(transformedMarkers, filtersTranslations);
            setStores(transformedMarkers);
            setFilters(filtersBaseOnResult);
        }
        catch (e) {
            console.error(e);
        }
    }, [filtersTranslations, flotiqApi, storesLimit]);
    const debouncedLoadMarkersFromApi = useCallback(debounce(handleLoadMarkersFromApi, 300), [handleLoadMarkersFromApi]);
    const handleResetFilters = () => {
        setSelectedFilters({});
        setIsFiltersOpened(false);
    };
    const handleChangeFilters = (filters) => {
        if (!filters) {
            return;
        }
        setSelectedFilters(filters);
        setIsFiltersOpened(false);
    };
    const handleToggleFilters = () => {
        setIsFiltersOpened((opened) => !opened);
    };
    const handleLocationError = (browserHasGeolocation) => {
        const message = browserHasGeolocation
            ? "Error: The Geolocation service failed."
            : "Error: Your browser doesn't support geolocation.";
        toast.error(message);
    };
    const getUserMarkerPosition = () => {
        if (navigator?.geolocation) {
            navigator.geolocation.getCurrentPosition((position) => {
                const pos = {
                    lat: position.coords.latitude,
                    lng: position.coords.longitude,
                };
                setUserMarkerPosition(pos);
            }, () => {
                handleLocationError(true);
            });
        }
        else {
            handleLocationError(false);
        }
    };
    const handleSelectStore = useCallback((storeId) => {
        setActiveStoreId(storeId);
    }, [setActiveStoreId]);
    useEffect(() => {
        setIsNoMatchingFlotiqStores(false);
        handlefilterStores();
    }, [handlefilterStores]);
    return {
        activeStoreId,
        userMarkerPosition,
        flotiqStores: filteredStores,
        isNoMatchingFlotiqStores,
        filters,
        selectedFilters,
        isFiltersOpened,
        isFiltersAvailable,
        selectedFiltersNumber,
        handleResetFilters,
        handleChangeFilters,
        handleToggleFilters,
        getUserMarkerPosition,
        getFlotiqStores: debouncedLoadMarkersFromApi,
        handleSelectStore,
        handleSetFlotiqStores: setStores,
    };
};
//# sourceMappingURL=useFlotiqStores.js.map